import type { ReactNode } from "react";
import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import SiteNav, { BookingButton, WriteMeLink } from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageSeo from "@/components/site/PageSeo";

export const CRM_BX_SLUG = "implementer-crm-bordeaux-hubspot-salesforce";
export const CRM_BX_PATH = `/blog/${CRM_BX_SLUG}`;
export const CRM_BX_TITLE = "Implémenter un CRM à Bordeaux en 2026 : HubSpot, Salesforce, méthode | CMO Partner";
export const CRM_BX_DESCRIPTION =
  "HubSpot, Salesforce ou Pipedrive ? Méthode en 6 étapes, durée, erreurs à éviter et choix du bon accompagnement pour réussir la mise en place d'un CRM dans une PME bordelaise.";
export const CRM_BX_H1 = "Implémenter un CRM à Bordeaux en 2026 : HubSpot, Salesforce et méthode pas à pas";
const URL = `https://www.cmopartner.fr${CRM_BX_PATH}`;

export const CRM_BX_FAQ = [
  { q: "Quel CRM choisir pour une PME à Bordeaux ?", a: "HubSpot convient à la plupart des PME B2B qui veulent aligner marketing et ventes dans un seul outil. Salesforce est plus adapté aux ETI et aux processus de vente complexes. Pipedrive ou Brevo suffisent pour les petites équipes. Le choix doit partir de votre processus commercial, pas de l'outil." },
  { q: "Combien de temps faut-il pour mettre en place un CRM ?", a: "Comptez généralement 60 à 90 jours pour un déploiement complet dans une PME : audit, migration, paramétrage, automatisations et formation." },
  { q: "Peut-on migrer vers HubSpot sans perdre ses données ?", a: "Oui. La migration est précédée d'un audit et d'un nettoyage de la base, puis testée sur un échantillon avant la bascule complète." },
  { q: "Faut-il un intégrateur CRM ou un Fractional CMO ?", a: "Un intégrateur si votre stratégie commerciale est déjà claire et que le besoin est technique. Un Fractional CMO si vous devez aussi définir le lead qualifié, les séquences, le scoring et piloter les résultats avec les équipes." },
  { q: "Intervenez-vous sur site à Bordeaux ?", a: "Oui. Les ateliers de cadrage et les formations se font en présentiel à Bordeaux et en Gironde ; le reste de la mission peut se faire à distance." },
];

const PERSON = { "@type": "Person", name: "Tiphany Rigaudias", jobTitle: ["Fractional CMO", "Directrice marketing externalisée", "Directrice digitale externalisée"], knowsAbout: ["Marketing B2B", "CRM", "Marketing automation", "IA appliquée au marketing", "Formation des équipes marketing"], url: "https://www.cmopartner.fr/a-propos", sameAs: ["https://linkedin.com/in/tiphanyrigaudias"] };

export const CRM_BX_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: CRM_BX_H1,
    description: CRM_BX_DESCRIPTION,
    datePublished: "2026-09-28",
    dateModified: "2026-09-28",
    inLanguage: "fr-FR",
    about: [{ "@type": "Thing", name: "CRM" }, { "@type": "Place", name: "Bordeaux" }],
    author: PERSON,
    publisher: { "@type": "Organization", name: "CMO Partner", url: "https://www.cmopartner.fr/", founder: PERSON },
    mainEntityOfPage: { "@type": "WebPage", "@id": URL },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: CRM_BX_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://www.cmopartner.fr/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.cmopartner.fr/blog" },
      { "@type": "ListItem", position: 3, name: "Implémenter un CRM à Bordeaux en 2026", item: URL },
    ],
  },
];

const H2 = ({ children }: { children: string }) => (
  <h2 className="text-2xl font-bold leading-tight md:text-3xl">
    {children}
    <span className="text-primary">.</span>
  </h2>
);
const H3 = ({ children }: { children: string }) => <h3 className="mt-10 text-xl font-bold">{children}</h3>;
const P = ({ children }: { children: ReactNode }) => <p className="mt-5 text-lg leading-relaxed text-muted-foreground">{children}</p>;
const A = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="text-foreground underline underline-offset-4 hover:text-primary">{children}</Link>
);
const Bullets = ({ items }: { items: string[] }) => (
  <ul className="mt-5 space-y-3">
    {items.map((t) => (
      <li key={t} className="flex gap-3 text-lg leading-relaxed text-muted-foreground">
        <span className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const questions = [
  "D'où viennent nos meilleurs clients ?",
  "Où les prospects décrochent-ils dans le tunnel de vente ?",
  "Quels leads les commerciaux doivent-ils appeler en priorité ?",
  "Combien rapporte réellement chaque action marketing ?",
];

const platforms: [string, string, string][] = [
  ["HubSpot", "Marketing et ventes dans un même outil, prise en main rapide, automatisations accessibles", "PME et ETI B2B qui veulent aligner marketing et commercial"],
  ["Salesforce", "Très personnalisable, adapté aux processus de vente complexes et multi-équipes", "ETI et organisations avec plusieurs équipes commerciales ou plusieurs pays"],
  ["Pipedrive", "Simple, centré sur le pipeline commercial", "Petites équipes commerciales qui démarrent"],
  ["Brevo", "Email marketing et automatisations à coût maîtrisé", "PME B2C et petites structures centrées sur l'email"],
];

const steps: [string, string][] = [
  ["1. Auditer l'existant", "Recensez vos sources de leads, vos fichiers clients, vos outils (site, formulaires, emailing, facturation) et la façon dont les commerciaux travaillent réellement. C'est ce qui évite de reproduire dans le CRM les défauts de l'organisation actuelle."],
  ["2. Définir le cycle de vie client et le lead qualifié", "Avant de paramétrer quoi que ce soit, marketing et ventes doivent se mettre d'accord sur les étapes (lead, MQL, SQL, opportunité, client, réactivation) et sur ce qu'est un lead qualifié. C'est l'étape la plus souvent oubliée, et la plus importante."],
  ["3. Nettoyer et migrer les données", "Dédoublonnage, normalisation des champs, suppression des contacts obsolètes, puis migration testée sur un échantillon avant la bascule complète. Une base propre au départ vaut mieux qu'une base exhaustive mais inutilisable."],
  ["4. Paramétrer pipelines, propriétés et tableaux de bord", "Un pipeline par processus de vente, des propriétés limitées à celles qui servent vraiment, et trois ou quatre tableaux de bord qui répondent aux questions du dirigeant."],
  ["5. Automatiser : scoring, nurturing, routage", "Lead scoring comportemental et démographique, séquences de nurturing par étape du cycle de vie, attribution automatique des leads aux commerciaux et notifications internes."],
  ["6. Former les équipes et piloter", "Formation des équipes marketing et commerciales, documentation des workflows, définition du délai de traitement des leads (SLA) et revue régulière des indicateurs. Un CRM se pilote : il ne se livre pas une fois pour toutes."],
];

const mistakes = [
  "Choisir l'outil avant d'avoir défini le processus commercial.",
  "Laisser l'intégrateur paramétrer sans décision marketing-sales sur le lead qualifié.",
  "Migrer toutes les données sans les nettoyer.",
  "Multiplier les propriétés et les workflows « au cas où ».",
  "Ne pas former ni accompagner les équipes après la mise en production.",
];

const readNext = [
  { label: "CRM & Lifecycle Marketing : l'offre", to: "/services/crm-lifecycle" },
  { label: "Stratégie marketing pour PME à Bordeaux", to: "/blog/strategie-marketing-pme-bordeaux" },
  { label: "Fractional CMO B2B SaaS", to: "/fractional-cmo-b2b-saas" },
];

const CrmBordeauxArticle = () => (
  <div className="min-h-screen bg-background text-foreground">
    <PageSeo path={CRM_BX_PATH} title={CRM_BX_TITLE} description={CRM_BX_DESCRIPTION} />
    <SiteNav />
    <main>
      <article>
        <header className="border-b border-border px-5 pb-14 pt-20 md:pt-28">
          <div className="mx-auto max-w-3xl">
            <nav className="mb-8 text-sm text-muted-foreground" aria-label="Fil d'Ariane">
              <Link to="/" className="hover:text-primary">Accueil</Link>
              <span className="px-2">/</span>
              <Link to="/blog" className="hover:text-primary">Blog</Link>
              <span className="px-2">/</span>
              <span className="text-foreground">Implémenter un CRM à Bordeaux en 2026</span>
            </nav>
            <h1 className="text-4xl font-bold leading-[1.1] md:text-5xl">{CRM_BX_H1}</h1>
            <p className="mt-8 text-sm text-muted-foreground">
              Par <Link to="/a-propos" className="font-semibold text-foreground hover:text-primary">CMO Partner — Fractional CMO</Link> · Mis à jour : <time dateTime="2026-09-28">septembre 2026</time>
            </p>
            <P>Beaucoup de PME de Gironde ont un CRM… et continuent de piloter leurs ventes sur des tableurs. Le problème vient rarement de l'outil : il vient de la façon dont il a été mis en place. Ce guide explique comment choisir la bonne plateforme, les étapes d'un déploiement réussi et les erreurs qui transforment un CRM en base de contacts inutilisée.</P>
          </div>
        </header>

        <div className="px-5 py-16 md:py-20">
          <div className="mx-auto max-w-3xl space-y-16">
            <section>
              <H2>Pourquoi un CRM change la donne pour une PME</H2>
              <P>Un CRM bien paramétré répond à quatre questions que se pose tout dirigeant :</P>
              <Bullets items={questions} />
              <P>Sans CRM structuré, ces réponses reposent sur l'intuition. Avec un CRM, elles s'appuient sur des données partagées par le marketing et les ventes.</P>
            </section>

            <section>
              <H2>HubSpot, Salesforce ou un autre CRM : comment choisir ?</H2>
              <div className="mt-8 overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[600px] text-left text-sm">
                  <thead className="bg-secondary">
                    <tr>{["Plateforme", "Points forts", "Pour qui"].map((h) => <th key={h} className="p-4 font-semibold">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {platforms.map(([n, s, w]) => (
                      <tr key={n} className="border-t border-border align-top">
                        <td className="p-4 font-semibold">{n}</td>
                        <td className="p-4 text-muted-foreground">{s}</td>
                        <td className="p-4 text-muted-foreground">{w}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <P>Le bon choix dépend de la complexité de votre cycle de vente, du nombre d'utilisateurs, de vos outils existants et de votre budget. Les tarifs des éditeurs évoluent régulièrement : vérifiez-les au moment du choix et raisonnez en coût total (licences, paramétrage, formation).</P>
            </section>

            <section>
              <H2>Implémenter un CRM en 6 étapes</H2>
              {steps.map(([t, d], i) => (
                <div key={t}>
                  <H3>{t}</H3>
                  <P>{d}</P>
                  {i === 2 && (
                    <div className="mt-10 rounded-lg bg-secondary p-7">
                      <p className="text-lg font-semibold">Faisons le point sur votre CRM en 30 minutes.</p>
                      <div className="mt-5"><BookingButton label="Réserver un appel" location="blog_crm_bx_mid" /></div>
                    </div>
                  )}
                </div>
              ))}
            </section>

            <section>
              <H2>Combien de temps prend un déploiement ?</H2>
              <P>Pour une PME, un déploiement complet (audit, paramétrage, automatisations, formation) prend généralement 60 à 90 jours. Les premières séquences actives produisent des résultats mesurables en 30 à 45 jours ; le système atteint sa pleine maturité en 3 à 6 mois.</P>
            </section>

            <section>
              <H2>Les 5 erreurs qui font échouer un projet CRM</H2>
              <Bullets items={mistakes} />
            </section>

            <section>
              <H2>Intégrateur CRM ou Fractional CMO : qui choisir ?</H2>
              <P>Un intégrateur CRM configure l'outil : c'est un expert technique, utile quand votre stratégie commerciale est déjà claire. Un Fractional CMO configure l'outil et définit ce qui va dedans : segments, messages, séquences, scoring, indicateurs. Il aligne aussi le marketing et les ventes et pilote les résultats après le déploiement.</P>
              <P>Si vous savez précisément ce que le CRM doit faire, un intégrateur suffit. Si le vrai sujet est « comment transformer nos leads en clients », c'est une mission de direction marketing. C'est l'approche de notre offre <A to="/services/crm-lifecycle">CRM & Lifecycle Marketing</A>.</P>
            </section>

            <section>
              <H2>Un accompagnement local à Bordeaux</H2>
              <P>Les ateliers de cadrage (définition du lead qualifié, cycle de vie, SLA) sont beaucoup plus efficaces en présentiel, avec le marketing et les ventes dans la même pièce. Basée à Bordeaux, CMO Partner intervient sur place en Gironde et en Nouvelle-Aquitaine (Bordeaux, Mérignac, Pessac, Bègles, Arcachon, Libourne), et à distance dans toute la France.</P>
              <P>Exemples de résultats : chez smsmode, la structuration du CRM et du nurturing a contribué à +50 % de leads inbound et +25 % de ROI marketing. Chez Lumivero, un pipeline multi-pays unifié a accompagné +30 % de croissance du chiffre d'affaires.</P>
            </section>
          </div>
        </div>
      </article>

      <section className="border-t border-border bg-secondary px-5 py-20">
        <div className="mx-auto max-w-3xl">
          <H2>FAQ : implémenter un CRM</H2>
          <div className="mt-10">
            {CRM_BX_FAQ.map((f) => (
              <details key={f.q} className="group border-t border-border py-6">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-bold">
                  {f.q}
                  <span className="text-primary transition-transform group-open:rotate-45" aria-hidden="true">+</span>
                </summary>
                <p className="mt-3 leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border px-5 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-xl font-bold">À lire aussi</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {readNext.map((r) => (
              <Link key={r.to} to={r.to} className="rounded-lg border border-border p-5 transition-colors hover:border-primary">
                <span className="font-semibold">{r.label}</span>
                <ArrowRight className="mt-4 h-4 w-4 text-primary" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="booking" className="scroll-mt-16 bg-foreground px-5 py-24 text-center text-background md:py-28">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold md:text-5xl">Votre CRM peut enfin convertir<span className="text-primary">.</span></h2>
          <p className="mt-6 text-lg text-background/60">30 minutes pour faire le point sur votre CRM et vos priorités.</p>
          <div className="mt-10"><BookingButton label="Réserver un appel de 30 minutes" location="blog_crm_bx_final" large /><WriteMeLink /></div>
          <p className="mt-6 text-sm text-background/50">Basée à Bordeaux · Disponible partout en France</p>
        </div>
      </section>
    </main>
    <SiteFooter />
  </div>
);

export default CrmBordeauxArticle;
